import { Box, keyframes } from '@mui/material';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';

import routeConfiguration from '../../../routeConfiguration';
import { findRouteByRouteName } from '../../../util/routes';
import IconCheckmark from '../../Icons/IconCheckmark/IconCheckmark';
import IconSpinner from '../../Icons/IconSpinner/IconSpinner';

// Marker classes applied to the child spinner/checkmark icons. Passing a (truthy)
// rootClassName to the icon suppresses the icon's own default sx, so its appearance is
// driven entirely by the descendant rules in the button's sx below.
const SPINNER_CLASS = 'ButtonSpinnerIcon';
const CHECKMARK_CLASS = 'ButtonCheckmarkIcon';

const checkmarkAnimation = keyframes({
  '0%': {
    opacity: 0,
    transform: 'scale(0.75)',
  },
  '50%': {
    transform: 'scale(1.15)',
  },
  '100%': {
    opacity: 1,
    transform: 'scale(1)',
  },
});

// Styles for the spinner/checkmark icons rendered inside the button. These always apply
// (independent of rootClassName), matching the original `css.spinner` / `css.checkmark`.
const iconSx = (theme) => ({
  [`& .${SPINNER_CLASS}`]: {
    width: '28px',
    height: '28px',
    stroke: theme.tokens.matterColorLight,
    strokeWidth: '3px',
  },
  [`& .${CHECKMARK_CLASS}`]: {
    width: '24px',
    height: '24px',
    stroke: theme.tokens.matterColorLight,
    // Interpolate the keyframes into a string (its generated name). Passing the raw
    // emotion keyframes OBJECT as animationName makes MUI styleFunctionSx traverse the
    // object's own keys (incl. its `toString`) and crash ("reading 'name'"). Matches the
    // working pattern in ManageAvailabilityCalendar / AnimatedLogo.
    animationName: `${checkmarkAnimation}`,
    animationDuration: '0.5s',
    animationIterationCount: 1,
    animationTimingFunction: 'ease-out',
  },
});

// Base button styles — applied only when no rootClassName override is passed.
// Mirrors `.root { @apply --marketplaceButtonStyles; padding: 0; }`.
const rootSx = (theme) => ({
  ...theme.mixins.marketplaceButtonStyles,
  padding: 0,
});

// State styles for `.ready` / `.inProgress` (identical declarations).
const stateSx = (theme) => ({
  padding: 0,
  '&:disabled': {
    backgroundColor: theme.tokens.marketplaceColor,
  },
});

// `.inlineTextButtonRoot { @apply --marketplaceLinkStyles; }` (the empty `.inlineTextButton`
// handle adds nothing).
const inlineTextButtonSx = (theme) => ({ ...theme.mixins.marketplaceLinkStyles });

/**
 * @param {Object} props
 * @param {React.ReactNode} [props.children]
 * @param {string} [props.className]
 * @param {string} [props.rootClassName]
 * @param {string} [props.spinnerClassName]
 * @param {string} [props.checkmarkClassName]
 * @param {boolean} [props.inProgress]
 * @param {boolean} [props.ready]
 * @param {boolean} [props.disabled]
 * @param {string} [props.size]
 * @param {string} [props.enforcePagePreloadFor]
 * @param {Object} [props.forwardedRef]
 */
const Button = ({
  children = null,
  className = null,
  rootClassName = null,
  spinnerClassName,
  checkmarkClassName,
  inProgress = false,
  ready = false,
  disabled = false,
  size,
  enforcePagePreloadFor = null,
  forwardedRef,
  sx: sxProp,
  ...rest
}) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  let content;

  if (inProgress) {
    content = <IconSpinner rootClassName={spinnerClassName || SPINNER_CLASS} />;
  } else if (ready) {
    content = <IconCheckmark rootClassName={checkmarkClassName || CHECKMARK_CLASS} size={size} />;
  } else {
    content = children;
  }

  const onOverButtonFn = (enforcePreloadOfPage) => () => {
    // Enforce preloading of given page (loadable component)
    const { component: Page } = findRouteByRouteName(enforcePreloadOfPage, routeConfiguration());
    // Loadable Component has a "preload" function.
    if (Page.preload) {
      Page.preload();
    }
  };

  const onOverButton = enforcePagePreloadFor ? onOverButtonFn(enforcePagePreloadFor) : null;
  const onOverButtonMaybe = onOverButton
    ? {
        onMouseOver: onOverButton,
        onTouchStart: onOverButton,
      }
    : {};

  // All buttons are disabled until the component is mounted. This
  // prevents e.g. being able to submit forms to the backend before
  // the client side is handling the submit.
  const buttonDisabled = mounted ? !!disabled : true;

  // The component's own base style (`css.root`) applies ONLY when no rootClassName override
  // and no caller-supplied sx root is passed (mirrors the original Sharetribe semantics).
  const applyBase = !rootClassName && !sxProp;

  const sx = (theme) => ({
    ...iconSx(theme),
    ...(applyBase ? rootSx(theme) : null),
    ...(ready || inProgress ? stateSx(theme) : null),
    ...(typeof sxProp === 'function' ? sxProp(theme) : sxProp),
  });

  return (
    <Box
      className={classNames(rootClassName, className) || undefined}
      component="button"
      ref={forwardedRef}
      sx={sx}
      {...onOverButtonMaybe}
      {...rest}
      disabled={buttonDisabled}
    >
      {content}
    </Box>
  );
};

export default Button;

export const InlineTextButton = ({ rootClassName = null, ...props }) =>
  // Original: rootClassName={classNames(rootClassName || css.inlineTextButtonRoot, css.inlineTextButton)}.
  // When a caller supplies rootClassName it replaces the link styling; otherwise the
  // `--marketplaceLinkStyles` set is applied via sx (which also suppresses the button base).
  rootClassName ? (
    <Button {...props} rootClassName={rootClassName} />
  ) : (
    <Button {...props} sx={inlineTextButtonSx} />
  );
InlineTextButton.displayName = 'InlineTextButton';
